import { useCallback } from "react";
import { useAxiosInstances } from "../axios-instance";
import { IResaleConfig } from "../../application/models";

export const useResaleConfigApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();

  const createResaleConfigApi = useCallback(
    (data: {
      companyId: string;
      name: string;
      img: string;
      favicon: string;
      loginPageImg: string;
      domainFrontEnd: string;
      domainBackEnd?: string;
      contact: {
        email: string;
        phone: string;
        beginHour: string;
        endHour: string;
      };
      emailProviderSettings: {
        authType: "simple" | "oauth2";
        user: string;
        password: string;
        host: string;
        port: string;
        oauth2?: {
          provider?: "google" | "microsoft";
          clientId: string;
          clientSecret: string;
          tenant?: string;
        };
      };
      theme: {
        colors: {
          primary: {
            main: string;
            contrastColor: string;
          };
          secondary: {
            main: string;
            contrastColor: string;
          };
          background: {
            paper: {
              main: string;
            };
            body: {
              main: string;
            };
          };
        };
        fontFamily?: string;
      };
    }): Promise<IResaleConfig> => {
      return axiosAuthInstance
        .post("/routes/resale-config", {
          resaleConfig: {
            companyId: data.companyId,
            name: data.name,
            img: data.img,
            favicon: data.favicon,
            loginPageImg: data.loginPageImg,
            domainFrontEnd: data.domainFrontEnd,
            domainBackEnd: data.domainBackEnd,
            contact: {
              email: data.contact.email,
              phone: data.contact.phone,
              beginHour: data.contact.beginHour,
              endHour: data.contact.endHour,
            },
            emailProviderSettings: {
              authType: data.emailProviderSettings.authType,
              user: data.emailProviderSettings.user,
              password: data.emailProviderSettings.password,
              host: data.emailProviderSettings.host,
              port: data.emailProviderSettings.port,
              oauth2: data.emailProviderSettings.oauth2,
            },
            theme: {
              colors: {
                primary: {
                  main: data.theme.colors.primary.main,
                  contrastColor: data.theme.colors.primary.contrastColor,
                },
                secondary: {
                  main: data.theme.colors.secondary.main,
                  contrastColor: data.theme.colors.secondary.contrastColor,
                },
                background: {
                  paper: {
                    main: data.theme.colors.background.paper.main,
                  },
                  body: {
                    main: data.theme.colors.background.body.main,
                  },
                },
              },
              fontFamily: data.theme.fontFamily,
            },
          },
        })
        .then((result) => {
          const resaleConfig = result.data.result.resaleConfig;

            if (resaleConfig) {
                return resaleConfig
            } else {
                throw {
                    type: 'NotFoundError',
                    message: 'Erro ao criar white label'
                }
            }
        }).catch((error) => {
            if (error?.response?.data?.result) {
                throw {
                    message: error.response.data.result.message,
                    type: error.response.data.result.type
                }
            } else {
                throw {
                    message: 'Erro desconhecido',
                    type: 'unknown'
                }
            }
        })
    }, [axiosAuthInstance])

    const findResaleConfigByCompanyIdApi = useCallback((data: {
        companyId: string
    }): Promise<IResaleConfig> => {
        return axiosAuthInstance.get(`/routes/resale-config/find/${data.companyId}`).then((result) => {
            const resaleConfig = result.data?.result?.resaleConfig

          if (resaleConfig) {
            return resaleConfig;
          } else {
            throw {
              type: "NotFoundError",
              message: "Configuração de white label não encontrada",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const findResaleConfigByResaleIdApi = useCallback(
    (data: { resaleId: string }): Promise<IResaleConfig> => {


      return axiosAuthInstance
        .get(`/routes/resale-config/${data.resaleId}`)
        .then((result) => {
          const resaleConfig = result.data?.result?.resaleConfig;

          if (resaleConfig) {
            return resaleConfig;
          } else {
            throw {
              type: "NotFoundError",
              message: "Configuração de white label não encontrada",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const updateResaleConfigApi = useCallback(
    (data: { resaleConfig: Partial<IResaleConfig> }) => {
      return axiosAuthInstance
        .put("/routes/resale-config", {
          resaleConfig: data.resaleConfig,
        })
        .then((result) => {
          const resaleConfig = result.data?.result?.resaleConfig;

          if (resaleConfig) {
            return resaleConfig;
          } else {
            throw {
              type: "NotFoundError",
              message: "Erro ao atualizar ambiente",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  const findResaleConfigByDomainApi = useCallback(
    (data: { domain: string }): Promise<IResaleConfig> => {
      return axiosAuthInstance
        .get(`/routes/resale-config/domain/${data.domain}`)
        .then((result) => {
          const resaleConfig = result.data?.result?.resaleConfig;

          if (resaleConfig) {
            return resaleConfig;
          } else {
            throw {
              type: "NotFoundError",
              message: "Configuração de white label não encontrada",
            };
          }
        })
        .catch((error) => {
          if (error?.response?.data?.result) {
            throw {
              message: error.response.data.result.message,
              type: error.response.data.result.type,
            };
          } else {
            throw {
              message: "Erro desconhecido",
              type: "unknown",
            };
          }
        });
    },
    [axiosAuthInstance]
  );

  return {
    createResaleConfigApi,
    findResaleConfigByCompanyIdApi,
    findResaleConfigByResaleIdApi,
    updateResaleConfigApi,
    findResaleConfigByDomainApi,
  };
};
